import { Injectable } from '@angular/core';
import { UtilService, LocalStorageService, NameValue } from '../../shared/index';
import { MassCommParamNav } from './mass-comm-param-nav.model';
import { NavigationService } from '../../shared/navigation/navigation.service';
import { ProfileAdvancedSearchParams } from 'src/app/profiles/search/profile-advanced-search-params.model';

@Injectable()
export class MassCommParameterService {
  private _utilService: UtilService;
  private _localStorageService: LocalStorageService;
  private _paramNameEmails: string;
  private _paramNameProfileIds: string;
  private _paramNameNavigatedFrom: string;
  private _paramNameSearchParams = 'massCommSearchParams';

  // from registrations, from attendees
  private _paramFromType: string = 'paramFromType';
  private _paramEventId: string = 'paramEventId';

  public constructor(utilService: UtilService, localStorageService: LocalStorageService) {
    this._utilService = utilService;
    this._localStorageService = localStorageService;

    this._paramNameEmails = 'createEmailParamEmails';
    this._paramNameProfileIds = 'createEmailParamProfileIds';
    this._paramNameNavigatedFrom = 'createEmailParamNavigatedFrom';
  }

  public hasParams = (): boolean => {
    if (
      this._localStorageService.hasItem(this._paramNameProfileIds) ||
      this._localStorageService.hasItem(this._paramNameEmails) ||
      this._localStorageService.hasItem(this._paramNameSearchParams)
    ) {
      return true;
    } else {
      return false;
    }
  };

  public clearNavigatedFromParams = (): void => {
    if (this._localStorageService.hasItem(this._paramNameNavigatedFrom)) {
      this._localStorageService.removeItem(this._paramNameNavigatedFrom);
    }
  };

  public getNavigatedFromParams = (): MassCommParamNav => {
    let navFrom = new MassCommParamNav();
    if (this._localStorageService.hasItem(this._paramNameNavigatedFrom)) {
      navFrom = this._localStorageService.getObject(this._paramNameNavigatedFrom);
    }
    return navFrom;
  };

  public setNavigatedFromParams = (navFrom: MassCommParamNav): void => {
    if (navFrom.NavigatedFromRoute.startsWith('/')) {
      navFrom.NavigatedFromRoute = navFrom.NavigatedFromRoute.substring(1, navFrom.NavigatedFromRoute.length);
    }
    this._localStorageService.setObject(this._paramNameNavigatedFrom, navFrom);
  };

  public hasNavigatedFrom = (): boolean => {
    if (this._localStorageService.hasItem(this._paramNameNavigatedFrom)) {
      return true;
    } else {
      return false;
    }
  };

  public clearEmailParams = (): void => {
    if (this._localStorageService.hasItem(this._paramNameEmails)) {
      this._localStorageService.removeItem(this._paramNameEmails);
    }
  };

  public hasEmailParams = (): boolean => {
    if (this._localStorageService.hasItem(this._paramNameEmails)) {
      return true;
    } else {
      return false;
    }
  };

  public clearProfileIdParams = (): void => {
    if (this._localStorageService.hasItem(this._paramNameProfileIds)) {
      this._localStorageService.removeItem(this._paramNameProfileIds);
    }

    if (this._localStorageService.hasItem(this._paramFromType)) {
      this._localStorageService.removeItem(this._paramFromType);
    }

    if (this._localStorageService.hasItem(this._paramNameNavigatedFrom)) {
      this._localStorageService.removeItem(this._paramNameNavigatedFrom);
    }
  };

  public hasProfileIdParams = (): boolean => {
    if (this._localStorageService.hasItem(this._paramNameProfileIds)) {
      return true;
    } else {
      return false;
    }
  };

  public setEmailParams = (emailAddresses: Array<NameValue>): void => {
    if (this._utilService.isNotNullOrUndefined(emailAddresses)) {
      if (emailAddresses.length > 0) {
        this._localStorageService.setObject(this._paramNameEmails, emailAddresses);
      }
    }
  };

  public setFromType(type: string): void {
    this._localStorageService.setObject(this._paramFromType, type);
  }

  public getFromType(): string {
    if (this._localStorageService.hasItem(this._paramFromType)) return this._localStorageService.getObject(this._paramFromType);
    else return '';
  }

  public setEventId(eventId: number): void {
    this._localStorageService.setObject(this._paramEventId, eventId);
  }

  public getEventId(): number {
    if (this._localStorageService.hasItem(this._paramEventId))
      return isNaN(parseInt(this._localStorageService.getObject(this._paramEventId)))
        ? 0
        : parseInt(this._localStorageService.getObject(this._paramEventId));
    else return 0;
  }

  public setProfileIdParams = (profileIds: Array<number>): void => {
    if (this._utilService.isNotNullOrUndefined(profileIds)) {
      if (profileIds.length > 0) {
        this._localStorageService.setObject(this._paramNameProfileIds, profileIds);
      }
    }
  };

  public getEmailParams = (): Array<NameValue> => {
    let param: Array<NameValue> = new Array<NameValue>();
    if (this._localStorageService.hasItem(this._paramNameEmails)) {
      let items: Array<NameValue> = this._localStorageService.getObject(this._paramNameEmails);
      if (this._utilService.isNotNullOrUndefined(items)) {
        return items;
      }
    }
    return param;
  };

  public getProfileIdParams = (): Array<number> => {
    let param = new Array<number>();
    if (this._localStorageService.hasItem(this._paramNameProfileIds)) {
      let items: Array<number> = this._localStorageService.getObject(this._paramNameProfileIds);
      if (this._utilService.isNotNullOrUndefined(items)) {
        param = items;
      }
    }

    return param;
  };

  public hasProfileSearchParams() {
    return this._localStorageService.hasItem(this._paramNameSearchParams);
  }

  public getProfileSearchParams(): ProfileAdvancedSearchParams {
    if (this._localStorageService.hasItem(this._paramNameSearchParams)) {
      return this._localStorageService.getObject(this._paramNameSearchParams);
    }
  }

  public setProfileSearchParams(params: ProfileAdvancedSearchParams) {
    this._localStorageService.setObject(this._paramNameSearchParams, params);
  }

  public clearProfileSearchParams(): void {
    if (this._localStorageService.hasItem(this._paramNameSearchParams)) {
      this._localStorageService.removeItem(this._paramNameSearchParams);
    }
  }
}
